import AbstractFilterControl from "./AbstractFilterControl";

import type { Criteria, EqualCriteria } from "@esWebAPI/es/model/Criteria";
import type LayerViewModel from "@esWebAPI/es/map/LayerViewModel";
import type FormatterFuncs from "@esWebAPI/es/util/formatterFuncs";

const SUPPORTED_ATTR = ['ES_AGGR_ID', 'ES_ID', 'ES_AGGR_VALUE'];

export default class AggregateFilterControl extends AbstractFilterControl {
    private analysis;
    private aggregateValue?: any;
    private layers?: any;

    private layerLabel;
    private valueLabel;

    constructor(layerViewModel: LayerViewModel, analysis, aggregateValue?: any, layerModule?: any) {
        super(layerViewModel, "Aggregate", "Drilldown Filter");

        this.analysis = analysis;
        this.aggregateValue = aggregateValue;
        this.layers = layerModule;
    }

    init(formatterFuncs: FormatterFuncs): JQueryDeferred<AbstractFilterControl> {
        if (this.analysis.$type === 'AggregateByAttributeAnalysis') {
            return es.api.layerView.get(this.analysis.generatedAggregateLayerViewId).then((aggrLayerView) => {
                this.layerLabel = aggrLayerView.title;
                this.valueLabel = this.aggregateValue;
                
                return this;
            });
        }
        return this.getLayerViewTitleAndAggregateValue(formatterFuncs).then((title, value) => {
            this.layerLabel = title;
            this.valueLabel = value;

            return this;
        });
    }

    private isMultipleCriteria(criteria: Criteria | Criteria[]) {
        return Array.isArray(criteria)
            && criteria.length > 0
            && criteria.every((crit) => {
                return crit.value === criteria[0].value
                        && crit.attr === criteria[0].attr;
            });
    }

    private mergeCriteria(criteria: Criteria[]) {
        return criteria[0];
    }

    getAttrName(other?: boolean): string {
        var seekedId = other ? this.analysis.generatedDrilldownLayerViewId : this.analysis.generatedAggregateLayerViewId;
        if (this.analysis.$type === 'AggregateByAttributeAnalysis') {
            return 'ES_AGGR_VALUE';
        } else if (this.layerViewModel.getId() === seekedId) {
            return 'ES_ID';
        } else {
            return 'ES_AGGR_ID';
        }
    }

    getLabel() {
        return this.layerLabel;
    }

    getValueLabel() {
        return this.valueLabel;
    }

    getOtherLayerCriteria(): EqualCriteria<any> {
        return {
            $type: 'Equal',
            value: this.aggregateValue,
            attr: this.getAttrName(true)
        };
    }

    affectOtherLayer(): boolean {
        return true;
    };

    toCriteria(): EqualCriteria<any> {
        return {
            $type: 'Equal',
            value: this.aggregateValue,
            attr: this.getAttrName()
        };
    }

    fromCriteria(criteria: Criteria | Criteria[]): AbstractFilterControl {
        if (this.isMultipleCriteria(criteria)) {
            criteria = this.mergeCriteria(criteria as Criteria[]);
        }
        
        this.aggregateValue = criteria.value;
        this.attrName = criteria.attr;
        // TODO FIXME what about attrType? and title?

        return this;
    }

    getOtherLayerId(): number {
        if (this.layerViewModel.getId() === this.analysis.generatedAggregateLayerViewId) {
            return this.analysis.generatedDrilldownLayerViewId;
        }

        return this.analysis.generatedAggregateLayerViewId;
    }

    getLayerViewTitleAndAggregateValue(formatterFuncs: FormatterFuncs): JQueryDeferred<any> {
        return $.when(
            es.api.layerView.get(this.analysis.generatedAggregateLayerViewId),
            es.api.layerInfo.getForLayerView(this.analysis.generatedAggregateLayerViewId),
            es.api.feature.get(this.analysis.generatedAggregateLayerViewId, this.aggregateValue, null, null, 4326)
        ).then((aggrLayerView, aggrLayerInfo, feature) => {
            if (!feature) {
                // The feature was removed, so we resolve empty
                return es.util.deferred.resolved(aggrLayerView.title);
            }
            
            var attr = aggrLayerInfo.attributes.find(a => a.name == aggrLayerView.styleSettings.labelSettings.labelAttrs[0]);
            var value = feature.attrs[aggrLayerView.styleSettings.labelSettings.labelAttrs[0]];
            switch (attr.attrType) {
                case "BOOLEAN":
                    value = formatterFuncs.bool(value);
                    break;

                case "INTEGER":
                case "DECIMAL":
                    value = formatterFuncs.number(value);
                    break;

                case "TIMESTAMP":
                    value = formatterFuncs.date(value);
                    break;
            }

            return es.util.deferred.resolved(aggrLayerView.title, value);
        });
    }

    static supportAttribute(): boolean {
        return false; //AggregateFilter always come from criteria
    }

    static supportCriteria(criteria: Criteria): boolean {
        return SUPPORTED_ATTR.indexOf(criteria.attr) !== -1 && criteria.$type === "Equal";
    }

    static fromCriteria(layerViewModel: LayerViewModel, criteria: Criteria, attribute, range, layerModule, analysis, formatterFuncs) {
        return new AggregateFilterControl(layerViewModel, analysis).fromCriteria(criteria).init(formatterFuncs).then((filter: AggregateFilterControl) => {
            return filter.fromCriteria(criteria);
        });
    }

    //es.control.AggregateFilter = AggregateFilter;
}
